import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  addExamSetWithQuestions,
  docxValidate,
  getExtractDocxQuestion,
  getPatternDetailList,
  getSubExam,
  getSubjectList,
} from "../../../Action/adminPanelAction";
import { useDispatch } from "react-redux";
import PageTitle from "../../../Module/pageTitle";
import Dropdown from "../../../Component/dropdown";
import { Copy, Play, RotateCw, Trash2, Wand2, X, Zap } from "lucide-react";
import {
  getExamListNew,
  getParentCourseNew,
  getSubTopicList,
  getTopicList,
} from "../../../Action/courseAction";
import { showErrorSnackbar } from "../../../Action/snackbarAction";
import "./AICommand.css";
import SubmitExamsetPopup from "./SubmitExamsetPopup";
import moment from "moment";
import LabelField from "../../../Component/labelField";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import SubjectNamePopUp from "../../../Component/subjectNamePopUp";
import { monthData, yearData1 } from "../../../Utils/dataConstants";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import ToggleSwitchBtn from "../../../Component/toggleSwitchBtn";
// ─── DOMPurify (lazy sanitizer) ───────────────────────────────────────────────
// Install: npm install dompurify
// If you can't install, the fallback stripUnsafe() below is used instead.
let _purify = null;
// Memo of sanitized HTML: the question list re-renders on every log line /
// keystroke, and re-sanitizing hundreds of blocks each time freezes the page
// with large (multi-file) extractions. Cleared when it grows too large.
const _sanitizeCache = new Map();
const SANITIZE_CACHE_LIMIT = 5000;
const sanitizeUncached = (html) => {
  if (typeof window === "undefined") return html;
  try {
    if (!_purify) {
      const DOMPurify = require("dompurify");
      _purify = DOMPurify;
    }
    return _purify.sanitize(html, { USE_PROFILES: { html: true } });
  } catch {
    // Fallback: strip script tags and event handlers
    return html
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/\bon\w+\s*=\s*["'][^"']*["']/gi, "")
      .replace(/javascript:/gi, "");
  }
};
const sanitize = (html) => {
  if (typeof html !== "string") return sanitizeUncached(html);
  const cached = _sanitizeCache.get(html);
  if (cached !== undefined) return cached;
  if (_sanitizeCache.size >= SANITIZE_CACHE_LIMIT) _sanitizeCache.clear();
  const clean = sanitizeUncached(html);
  _sanitizeCache.set(html, clean);
  return clean;
};

// Stable MathJax config (module-level so it isn't recreated every render).
const MATHJAX_CONFIG = {
  tex: {
    inlineMath: [["\\(", "\\)"]],
    displayMath: [["\\[", "\\]"]],
    packages: { "[+]": ["mhchem"] },
  },
  loader: {
    load: ["[tex]/mhchem"],
  },
  options: {
    skipHtmlTags: ["script", "noscript", "style", "textarea"],
  },
};

// And in the user message, add a forcing function:
// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const EXTRACT_TIMEOUT_MS = 15 * 60 * 1000; // 3 minutes per batch

// ─── JSON PARSER ──────────────────────────────────────────────────────────────

function safeParseJSON(raw) {
  if (!raw || typeof raw !== "string") return null;

  // Try direct parse first
  try {
    return JSON.parse(raw);
  } catch { }

  // Extract JSON portion
  const first = raw.indexOf("{");
  const last = raw.lastIndexOf("}");

  if (first === -1 || last === -1) {
    return null;
  }

  const cleaned = raw.slice(first, last + 1);

  // Try parsing complete JSON again
  try {
    return JSON.parse(cleaned);
  } catch (e) {
    console.warn("Direct parse failed:", e.message);
  }

  // Fallback: merge multiple JSON objects
  const allQuestions = [];
  let depth = 0;
  let start = -1;

  for (let i = 0; i < cleaned.length; i++) {
    if (cleaned[i] === "{") {
      if (depth === 0) start = i;
      depth++;
    } else if (cleaned[i] === "}") {
      depth--;

      if (depth === 0 && start !== -1) {
        const chunk = cleaned.slice(start, i + 1);

        try {
          const parsed = JSON.parse(chunk);

          if (parsed?.questions?.length) {
            allQuestions.push(...parsed.questions);
          }
        } catch { }

        start = -1;
      }
    }
  }

  return allQuestions.length
    ? { questions: allQuestions }
    : null;
}

// Default exam set name from a file name: drop the extension, turn _ and -
// into spaces, collapse repeats. "SSC_CGL_TIER1_Mock24_Final.docx" →
// "SSC CGL TIER1 Mock24 Final".
const fileNameToSetName = (name = "") =>
  name
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function QuestionExtractor() {
  const dispatch = useDispatch();

  // File queue (same model as pyqExtraction): every uploaded file is
  // extracted on its own, keeps its own questions and is saved as its own
  // exam set — questions from different files are never merged.
  // Entry shape: { id, file, fileType, preview, status, questions, saved,
  //                sopValidated, sopErrors }
  // status: "pending" | "extracting" | "done" | "failed"
  const [files, setFiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [stage, setStage] = useState("idle"); // idle|uploading|counting|extracting|done
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("preview");
  const [dragOver, setDragOver] = useState(false);
  // The selected file drives the Preview/Questions/JSON panels, the
  // "Validate" button and "Save Data". SOP validation is manual and
  // per-file; sopErrors holds the severity "error" issues so admins see
  // exactly what to fix.
  const selectedEntry =
    files.find((entry) => entry.id === selectedId) || files[0] || null;
  const file = selectedEntry?.file || null;
  const fileType = selectedEntry?.fileType || null;
  const preview = selectedEntry?.preview || null;
  const questions = selectedEntry?.questions || [];
  const sopErrors = selectedEntry?.sopErrors || [];
  const sopValidated = selectedEntry?.sopValidated ?? null; // null | "pass" | "fail"
  const isMultiFile = files.length > 1;
  const updateFileEntry = (id, patch) =>
    setFiles((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
    );
  // Which file(s) the Save popup is saving — fixed when Save / Save All is
  // clicked so a selection change while the popup is open can't save the
  // wrong file.
  const saveTargetIdsRef = useRef([]);
  const [isSaving, setIsSaving] = useState(false);
  const [validatingDocx, setValidatingDocx] = useState(false);
  const [turnsUsed, setTurnsUsed] = useState(0);

  const fileRef = useRef();
  const fileIdRef = useRef(null); // FIX: ref instead of state — avoids stale closure race condition
  const abortRef = useRef(null); // FIX: AbortController for cancel support

  // ── Logging ───────────────────────────────────────────────────────────────

  const addLog = useCallback((msg, type = "info") => {
    const entry = { msg, type, time: new Date().toLocaleTimeString() };
    setLogs((prev) => [...prev, entry]); // FIX: functional updater — no logRef needed
  }, []);

  const [aiOutput, setAiOutput] = useState(
    "// AI command output will appear here... // Select a test type template or type your command",
  );

  const [totalQuestions, setTotalQuestions] = useState(10);
  const [questionTypes, setQuestionTypes] = useState("");
  const [isSectionWiseActive, setIsSectionWiseActive] = useState(false);
  const [setCount, setSetCount] = useState(1);
  const [exam, setExam] = useState("");
  const [subExam, setSubExam] = useState("");
  const [examStream, setExamStream] = useState("");
  const [subExamList, setSubExamList] = useState([]);
  const [examStreamList, setExamStreamList] = useState([]);
  const [examList, setExamList] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [patternList, setPatternList] = useState([]);
  const [selectedQuestionTypeList, setSelectedQuestionTypeList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [examSetName, setExamSetName] = useState("");
  const imageMapRef = useRef({});
  const imagePositionsRef = useRef({});
  const [selectedSubjectList, setSelectedSubjectList] = useState([]);
  const [topicList, setTopicList] = useState([]);
  const [subjectName, setSubjectName] = useState("");
  const [topicName, setTopicName] = useState("");
  const [examOpen, setExamOpen] = useState(false);
  const [subTopicList, setSubTopicList] = useState([]);
  const [subTopicName, setSubTopicName] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  let tempSubject = selectedSubjectList?.filter((item) => item?.isSelected);
  const templates = [
    { id: 1, name: "Mock Test", icon: "📋", color: "#2563eb", type: 2 },
    { id: 12, name: "Free User Mock Test", icon: "📋", color: "#2563eb", type: 2 },
    { id: 2, name: "DPP", icon: "📚", color: "#7c3aed", type: 3 },
    { id: 7, name: "Unit Test", icon: "✓", color: "#f59e0b", type: 1 },
    {
      id: 3,
      name: "Unit Wise Practice Test",
      icon: "✓",
      color: "#f59e0b",
      type: 1,
    },
    { id: 4, name: "Unit Wise PYQ Test", icon: "✓", color: "#f59e0b", type: 1 },
    {
      id: 8,
      name: "Free User Unit Wise",
      icon: "✓",
      color: "#f59e0b",
      type: 1,
    },
    {
      id: 13,
      name: "Topic Wise Practice Test",
      icon: "🎯",
      color: "#ec4899",
      type: 8,
    },
    {
      id: 5,
      name: "Sub Topic Wise Practice Test",
      icon: "🎯",
      color: "#ec4899",
      type: 3,
    },
    {
      id: 6,
      name: "Topic Wise PYQ Test",
      icon: "🎯",
      color: "#ec4899",
      type: 3,
    },
    {
      id: 9,
      name: "Free User Topic Test",
      icon: "🎯",
      color: "#ec4899",
      type: 3,
    },
    { id: 10, name: "Sunday Test", icon: "⭕", color: "#6366f1", type: 6 },
    // { id: 11, name: "PYQ Test", icon: "⭕", color: "#6366f1", type: 4 },
    // { id: 7, name: "PYQ Topic Test", icon: "📌", color: "#d946ef",type:2 },
    // { id: 8, name: "Scholarship Test", icon: "🏆", color: "#eab308",type:2 },
    // { id: 9, name: "Free Student Test", icon: "🎁", color: "#06b6d4" },
  ];

  // ─── PROMPTS ──────────────────────────────────────────────────────────────────

  const scaledPattern = scalePattern(
    questionTypes?.sectionData || [],
    totalQuestions,
  );

  const examPatternId = questionTypes?._id ?? "";
  const sectionDataForPrompt = isSectionWiseActive
    ? (questionTypes?.sectionData ?? [])
    : scaledPattern;

  const buildRangeLookup = (sectionData) => {
    const rows = [];
    sectionData.forEach((section) => {
      (section.parts || []).forEach((part) => {
        rows.push(
          `  Q${part.questionFrom}–Q${part.questionTo}` +
          ` → sectionId="${section._id}"` +
          ` | partId="${part._id}"` +
          ` | marks=${part.correct}` +
          ` | negativeMarks=${part.negative}`
        );
      });
    });
    return rows.join("\n");
  };

  const SYSTEM_PROMPT_TEXT = `
You are a JSON generator.
 
Return ONLY valid JSON.
No text before or after JSON.
 
FORMAT:
{"questions":[...]}
 
OUTPUT RULE:
- Return ONLY valid JSON
- NO markdown
- NO explanation
- JSON must be complete
 
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🚫 ABSOLUTE IMAGE URL PROHIBITION — HIGHEST PRIORITY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 
NEVER generate or use any URL for images. This includes:
  ❌ https://...
  ❌ http://...
  ❌ s3://...
  ❌ Any domain, path, or file URL string
 
The ONLY valid image format is:
  { "descType": "img", "description": "<image_placeholder data-key=\\"img_X\\">" }
 
Where img_X comes from the AVAILABLE IMAGES section below.
ANY URL in your output = INVALID. Reject and use placeholder keys only.
 
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📌 TITLE vs OPTION IMAGE PLACEMENT — CRITICAL RULE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 
Before placing any image, identify WHERE it appears in the PDF:
 
CASE 1 — Each option (A/B/C/D) is its own chemical structure / drawing:
  → title  = [ { "descType": "text", "description": "<p>question text</p>" } ]  ← NO image in title
  → Each option gets its OWN img block with a sequential key:
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_0\\">", "isCorrect": false }
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_1\\">", "isCorrect": false }
    ...

    ⚠️ CRITICAL — IF OPTIONS ARE IMAGES IN THE PDF:
Even if the option appears to be a single letter or simple symbol inside a box/shape,
if it is rendered as an IMAGE in the PDF (not plain text), it MUST use descType: "img".
NEVER convert image options to text. If the PDF shows options as drawn boxes, shapes,
or figures — use img blocks regardless of how simple the content looks.
 
CASE 2 — A single diagram appears in the question body (before the options):
  → title  = [
      { "descType": "text", "description": "<p>question text</p>" },
      { "descType": "img",  "description": "<image_placeholder data-key=\\"img_X\\">" }
    ]
  → options = text-only blocks
 
CASE 3 — Both a title diagram AND image options exist:
  → Include img block in title AND separate img block per option.
 
❌ WRONG — option image placed in title:
  "title": [
    { "descType": "text", "description": "<p>Which carbocation is most stable?</p>" },
    { "descType": "img",  "description": "<image_placeholder data-key=\\"img_0\\">" }
  ]
 
✅ CORRECT — text-only title, each option is its own image:
  "title": [
    { "descType": "text", "description": "<p>Which carbocation is most stable?</p>" }
  ],
  "options": [
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_0\\">", "isCorrect": false },
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_1\\">", "isCorrect": false },
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_2\\">", "isCorrect": true  },
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_3\\">", "isCorrect": false }
  ]
 
RULES:
1. Return ONLY questions requested in range.
2. "order" must match question number.
3. Each question must follow this structure:
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 SECTION ASSIGNMENT — MANDATORY LOOKUP TABLE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

examPatternId = "${examPatternId}"

FOR EVERY QUESTION: find its order number in the table below.
Use the sectionId, partId, marks, and negativeMarks from that row.
This is the ONLY way to assign sections — do NOT use topic names or guessing.

${buildRangeLookup(sectionDataForPrompt)}

ASSIGNMENT ALGORITHM:
  1. Look at the question's "order" number.
  2. Find the row where order is between Q_from and Q_to (inclusive).
  3. Copy sectionId, partId, marks, negativeMarks exactly from that row.
  4. Set examPatternId = "${examPatternId}"

VALIDATION:
  ✅ sectionId MUST be a real value from the table above — NEVER ""
  ✅ partId MUST be a real value from the table above — NEVER ""
  ✅ marks MUST equal the correct value from the table — NEVER 0 unless table says 0
  ❌ Do NOT assign a question to a section whose range excludes its order number
  ❌ Do NOT use topic/subject name to decide the section

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📐 QUESTION STRUCTURE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
QUESTION TYPE:
  - questionType 1 = MCQ (Single correct answer)
  - questionType 2 = MSQ (Multiple correct answers)
  - questionType 3 = NAT (Numerical Answer Type)
 
complexity Level:
  - complexityLevel 1 = EASY
  - complexityLevel 2 = MEDIUM
  - complexityLevel 3 = HARD
{
  "complexityLevel": 1-3,
  "createdByAI": true,
  "unit": "topic",
  "order": number,
  "title": [ ...blocks ],
  "options": [
    { "descType": "text"|"img", "description": "<p>...</p>"|"<image_placeholder data-key=\\"KEY\\">", "isCorrect": true/false }
  ],
  "questionType": 1|2|3,
  "marks": number,
  "negativeMarks": number,
  "ansDescription": { "text": "<p>FULL explanation here — include axiom, deduction, why each wrong option is wrong, exam connection, speed hack, and remember note.</p>", "image": "", "video": "" },
  "decimalPlaces": number,
  "range": { "r1": 0, "r2": null },
  "isPYQ": false,
  "section": {
    "examPatternId": "${examPatternId}",
   "sectionId": "<from lookup table>",
    "partId": "<from lookup table>"
  }
}
 
─── MULTI-BLOCK RULE (title, options, ansDescription) ───────────────────────
 
"title" and each option's content may contain MULTIPLE blocks in the order they appear.
Each block is either:
  { "descType": "text", "description": "<p>text here</p>" }
  { "descType": "img",  "description": "<image_placeholder data-key=\\"KEY\\">" }
 
Rules:
- Preserve the exact order of text and image as they appear in the source.
- If a question title is: [text] → [image] → [text], produce 3 blocks in that order.
- If a question title is text only, produce a single text block.
- If a question title is image only, produce a single image block.
- Apply the same multi-block logic to each option and to ansDescription.text.
- "ansDescription.text" MUST contain the COMPLETE answer explanation. Include ALL of the following:
    • Axiom / principle
    • Step-by-step deduction
    • Why each wrong option is wrong
    • Exam connection
    • Speed hack
    • Remember note
 
OPTION IMAGE RULE:
If the options in a question are structural/chemical drawings (not text),
each option MUST use descType: "img" — never describe the structure as text.
If multiple options share one combined image, still use individual img blocks
per option using the same or sequential image keys.
NEVER convert image options to text even if the option content is a single letter or number.
If the option is a drawn/rendered image in the PDF → it MUST be descType: "img".
NEVER substitute text descriptions for image options.
 
MANDATORY FORMAT — use exactly this syntax:
  { "descType": "img", "description": "<image_placeholder data-key=\\"KEY\\">" }
 
IMAGE INSERTION RULE (CRITICAL):
- If the question text says "based on the chart/graph/diagram/table below"
  OR "refer to the figure/diagram/table below" OR similar →
  you MUST insert an img block in the title AFTER the text block.
- NEVER drop the image reference from the title.
- NEVER remove "Based on..." or "Refer to..." from the question text.
- The text block AND the img block must BOTH appear in the title array.
 
EXAMPLE — correct title with image:
  "title": [
    { "descType": "text", "description": "<p>Based on the chart shown below, what is X?</p>" },
    { "descType": "img",  "description": "<image_placeholder data-key=\\"KEY\\">" }
  ]
 
EXAMPLE — title with image between text:
  "title": [
    { "descType": "text", "description": "<p>Consider the circuit shown below:</p>" },
    { "descType": "img",  "description": "<image_placeholder data-key=\\"KEY\\">" },
    { "descType": "text", "description": "<p>What is the current through R1?</p>" }
  ]
 
EXAMPLE — ansDescription with image:
  "ansDescription": {
    "text": "<p>See the diagram above.</p>",
    "image": "<image_placeholder data-key=\\"KEY\\">",
    "video": ""
  }
 
━━━━━━━━━━━━━━━━━━━━━━━
📐 EQUATION FORMAT RULES:
━━━━━━━━━━━━━━━━━━━━━━━
- Wrap inline equations with \( ... \)  → example: \(e^{-E/kT}\)
- Wrap display equations with \[ ... \] → example: \[\langle E \rangle = \frac{E e^{-E/kT}}{Z}\]
- NEVER write plain-text math like e^(−E/kT) or (M/ℏ)²
- Greek letters: \alpha \beta \varepsilon \hbar \theta \phi
- Fractions: \frac{numerator}{denominator}
- Subscripts: E_{1}   Superscripts: e^{-\beta\varepsilon}
 
─── SECTION DATA ─────────────────────────────────────────────────────────────
 
${JSON.stringify(sectionDataForPrompt)}
 
Each section has "_id" (sectionId) and "parts" array. Each part has:
- "_id" (partId)
- "questionType" (1 = MCQ, 2 = MSQ, 3 = NAT)
- "correct" (marks for correct answer)
- "negative" (negative marks for wrong answer)
 
─── IMPORTANT ────────────────────────────────────────────────────────────────
 
- "section.sectionId" → "_id" of the matching section
- "section.partId"    → "_id" of the part whose "questionType" matches this question
- "marks"             → value of "correct" from the matching part
- "negativeMarks"     → value of "negative" from the matching part
- MCQ → 4 options, only 1 isCorrect: true
- MSQ → 4 options, multiple isCorrect: true
- NAT → options = []
- Wrap all text in <p></p>
- Use MathJax \\(...\\)
- Escape backslashes properly (\\\\)
- decimalPlaces: NAT only (0, 1, 2, or 3); always 0 for MCQ/MSQ
- Section question counts are STRICT and MANDATORY.
- Generate EXACTLY "totalQuestions" for each section.
- Do NOT move questions between sections.
- Respect questionFrom and questionTo ranges strictly.
 
NAT RULE (STRICT DECISION LOGIC):
 
- questionType = 3
- options = []
 
STEP 1: Identify answer type
 
IF the problem has exact computable value / unique numerical answer / standard formula result:
  r1 = exact value, r2 = null
 
IF the problem has inequality / tolerance / uncertainty / asks for "range"/"interval"/"between":
  r1 = lower bound, r2 = upper bound
 
STEP 2: Validation
- Exact answer exists → DO NOT return range
- Range required → r2 MUST be > r1
- Not explicitly required → ALWAYS use fixed value
 
STEP 3: decimalPlaces — match precision: Integer → 0, Decimal → 1–3
 
INVALID: returning range when exact answer exists | r1 = 0 and r2 ≠ 0 for NAT
`;


  useEffect(() => {
    dispatch(
      getExamListNew((res) => {
        setExamList(res);
      }),
    );
  }, []);

  const onChangeExam = (value) => {
    setExam(value);
    setSubExam("");
    setSubExamList([]);
    setExamStream("");
    if (value) {
      dispatch(
        getSubExam({ examTypeId: value._id }, (res) => {
          if (res.data.length > 0) {
            setSubExamList(res.data);
          } else {
            dispatch(
              getParentCourseNew(`${value._id}`, (res) => {
                setExamStreamList(res);
              }),
            );
          }
        }),
      );
    }
  };

  const onChangeSubExam = (value) => {
    setSubExam(value);
    setExamStream("");
    if (value) {
      dispatch(
        getParentCourseNew(`${exam._id}/${value._id}`, (res) => {
          setExamStreamList(res);
        }),
      );
    }
  };

  const onChangeExamStream = (value) => {
    setExamStream(value);
    if (value) {
      let postData = {
        examTypeId: exam?._id,
        baseCourseId: value?._id,
      };
      if (subExam) {
        postData["subExamTypeId"] = subExam?._id;
      }
      dispatch(
        getSubjectList(postData, (res) => {
          let tempSubject = res?.data?.map((item) => {
            return {
              _id: item?._id,
              name: item?.name,
              isSelected: false,
            };
          });
          setSelectedSubjectList(tempSubject);
        }),
      );
    }
  };
  const getQuestionTypesFromSections = (sections = []) => {
    const types = new Set();

    sections.forEach((section) => {
      section.parts?.forEach((part) => {
        if (part?.questionTypeName) {
          types.add(part.questionTypeName);
        }
      });
    });

    return Array.from(types);
  };

  const onChangeQuestionPattern = (val) => {
    setQuestionTypes(val);
    setIsSectionWiseActive(val?.isModelSet);
    const questionTypeList = getQuestionTypesFromSections(val?.sectionData);
    setSelectedQuestionTypeList(questionTypeList);
    // setTotalQuestions(val?.totalQuestions);
  };

  const changeToggle = (e) => {
    if (e.target.checked) {
      setIsSectionWiseActive(true);
    } else {
      setIsSectionWiseActive(false);
    }
  };

  const handleTemplateClick = (templateName) => {
    setTotalQuestions(10);
    setSetCount(1);
    setQuestionTypes(null);
    setSelectedQuestionTypeList([]);
    setActiveTemplate(templateName);
    const isMockTest =
      templateName?.name === "Mock Test" || templateName?.name === "Free User Mock Test" || templateName?.name === "PYQ Test";
    const postData = {
      isModelSet: isMockTest,
      // display: isMockTest,
      ...(isMockTest && {
        examTypeId: exam?._id,
        baseCourseId: examStream?._id,
      }),
    };
    dispatch(
      getPatternDetailList(postData, (res) => {
        if (!res) return;
        setPatternList(res);
        const data = Array.isArray(res) ? res[0] : res;
        // if (isMockTest) {
        //   setQuestionTypes(data);
        //   setTotalQuestions(data?.totalQuestions || "");

        //   const questionTypeList = getQuestionTypesFromSections(
        //     data?.sectionData || [],
        //   );

        //   setSelectedQuestionTypeList(questionTypeList);
        // }
      }),
    );
  };

  /************************************************* */

  const callAPIPromise = (
    targetFile,
    systemPromptArr,
    promptArr,
    signal,
    mode = "extract",
  ) =>
    new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }

      const onAbort = () => reject(new DOMException("Aborted", "AbortError"));
      signal?.addEventListener("abort", onAbort, { once: true });
      const isPracticeTest = activeTemplate?.id === 3 || activeTemplate?.id === 5 || activeTemplate?.id === 13;
      const isPYQ =
        activeTemplate?.id === 4 ||
        activeTemplate?.id === 6 ||
        activeTemplate?.id === 11;
      const payload = {
        examType: activeTemplate?.type,
        isFreeUser: activeTemplate?.id === 12 || activeTemplate?.id === 8 || activeTemplate?.id === 9,
        isPracticeTest,
        isPYQ,
        examPatternId,
        systemPrompt: systemPromptArr,
        prompt: promptArr,
        mode,
      };
      if ([2, 5, 6, 9].includes(activeTemplate?.id)) {
        payload.topicId = topicName?._id;
        payload.subTopicId = subTopicName?._id;
      }
      if ([13].includes(activeTemplate?.id)) {
        payload.topicId = topicName?._id;
      }
      if (![1, 11].includes(activeTemplate?.id)) {
        payload.subjectId = tempSubject[0]?._id;
      }
      dispatch(
        getExtractDocxQuestion(
          targetFile, payload,
          (res) => {
            signal?.removeEventListener("abort", onAbort);
            if (signal?.aborted) {
              reject(new DOMException("Aborted", "AbortError"));
              return;
            }
            if (!res) {
              reject(new Error("No response from API"));
              return;
            }

            const content = res?.content ?? res?.data?.content ?? [];
            const textBlock = Array.isArray(content)
              ? content.find((b) => b.type === "text")
              : null;

            const text =
              textBlock?.text ??
              (typeof res?.output === "string" ? res.output : "") ??
              (typeof res === "string" ? res : "");

            const turns = textBlock?.turns ?? res?.turns ?? null;
            const usage = res?.usage ?? res?.data?.usage ?? null;

            if (!text) {
              reject(new Error("API returned empty text"));
              return;
            }
            resolve({ text, turns, usage });
          },
        ),
      );
    });

  // ── File handling ─────────────────────────────────────────────────────────

  const getFileType = (f) => {
    const name = f.name.toLowerCase();
    if (f.type.startsWith("image/")) return "image";
    if (f.type === "application/pdf") return "pdf";
    if (
      f.type === "application/msword" ||
      f.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      name.endsWith(".doc") ||
      name.endsWith(".docx")
    )
      return "docx";
    return null;
  };

  // Appends picked/dropped files to the queue; unsupported types are skipped.
  const handleFiles = (fileList) => {
    const entries = Array.from(fileList || []).reduce((acc, f) => {
      const type = getFileType(f);
      if (type) {
        acc.push({
          id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
          file: f,
          fileType: type,
          // Only images render inline; other types just need a truthy
          // value so the Preview tab shows the file name card.
          preview: type === "image" ? URL.createObjectURL(f) : f.name,
          status: "pending",
          questions: [],
          saved: false,
          // Per-file exam set name (defaults to the file name) and question
          // count used when saving; both editable on the file row.
          examSetName: fileNameToSetName(f.name),
          manualTotal: "",
          sopValidated: null,
          sopErrors: [],
        });
      }
      return acc;
    }, []);
    if (!entries.length) return;

    setFiles((prev) => [...prev, ...entries]);
    setSelectedId(entries[0].id);
    setActiveTab("preview");
  };

  const removeFile = (id) => {
    const entry = files.find((e) => e.id === id);
    if (entry?.fileType === "image") URL.revokeObjectURL(entry.preview);
    setFiles((prev) => prev.filter((e) => e.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const removeAllFiles = () => {
    files.forEach((e) => {
      if (e.fileType === "image") URL.revokeObjectURL(e.preview);
    });
    setFiles([]);
    setSelectedId(null);
  };

  const selectFile = (id) => {
    setSelectedId(id);
    const entry = files.find((e) => e.id === id);
    setActiveTab(entry?.questions?.length ? "questions" : "preview");
  };

  // ── Upload once, cache fileId in ref ──────────────────────────────────────

  const extractPdfImagesPromise = (f) =>
    new Promise((resolve) => {
      const formData = new FormData();
      formData.append("files", f);
      const url = process.env.REACT_APP_BASEURL + "/v1/claudeAI/extract-pdf-images";
      fetch(url, {
        method: "POST",
        body: formData,
      })
        .then((r) => r.json())
        .then((res) => {
          resolve({
            imageMap: res?.data?.imageMap || {},
            imagePositions: res?.data?.imagePositions || {}, // ← ADD
            totalImages: res?.data?.totalImages || 0,
          });
        })
        .catch(() => resolve({
          imageMap: {},
          imagePositions: {},
          totalImages: 0,
        }));
    });

  const replacePlaceholders = (questionsList, imageMap) => {
    if (!Object.keys(imageMap).length) return questionsList;

    const replaceInHtml = (html) => {
      if (!html || typeof html !== "string") return html;
      return html.replace(
        /<image_placeholder[^>]*data-key="([^"]+)"[^>]*\/?>/g,
        (_, key) => {
          const url = imageMap[key];
          return url
            ? `${url}`
            : `<span style="color:#f87171;font-size:11px">[Image: ${key} not found]</span>`;
        },
      );
    };

    const replaceInBlocks = (blocks) =>
      (blocks || []).map((block) => {
        if (block.descType !== "img") return block;
        const keyMatch = block.description?.match(/data-key="([^"]+)"/);
        const key = keyMatch?.[1];
        const url = key ? imageMap[key] : null;
        return {
          ...block,
          description: url
            ? `${url}`
            : `<span style="color:#f87171">[Image not found${key ? `: ${key}` : ""}]</span>`,
        };
      });

    return questionsList.map((q) => ({
      ...q,
      title: replaceInBlocks(q.title),
      options: (q.options || []).map((opt) => ({
        ...opt,
        description:
          opt.descType === "img" || opt.descType === "image"
            ? replaceInBlocks([opt])[0]?.description
            : replaceInHtml(opt.description),
      })),
      ansDescription: {
        ...q.ansDescription,
        text: replaceInHtml(q.ansDescription?.text),
      },
    }));
  };

  const ensureFileUploaded = async () => {
    if (fileIdRef.current) return fileIdRef.current;

    addLog("📤 Uploading file & extracting images...", "info");

    const [imageRes] = { imageMap: {}, imagePositions: {}, totalImages: 0 };

    // fileIdRef.current = anthropicRes.fileId;
    imageMapRef.current = imageRes?.imageMap || {};
    imagePositionsRef.current = imageRes?.imagePositions || {}; // ← ADD THIS

    const imgCount = imageRes?.totalImages || 0;

    return imgCount;
  };

  // ── Build prompt / system arrays ──────────────────────────────────────────

  const buildSystemPrompt = (text) => [
    { type: "text", text, cache_control: { type: "ephemeral" } },
  ];

  const buildPrompt = (instructionText) => [
    { type: "text", text: instructionText },
  ];

  // ── Cancel extraction ─────────────────────────────────────────────────────

  const cancelExtraction = () => {
    abortRef.current?.abort();
    addLog("🛑 Extraction cancelled by user.", "warn");
    setStage("idle");
  };

  // ── Validate docx against SOP rules (manual trigger) ───────────────────────
  // The response is its own report shape ({ ok, issues: [{ severity, message,
  // fix }] }), not the app's usual {statusCode, data, msg} envelope, so we
  // branch on `res.ok` directly and only surface issues whose severity is
  // "error" (warnings/infos don't block extraction).
  // Validates one queue entry; resolves true (pass) / false (fail). The
  // result is stored on that entry by id, so selection changes mid-request
  // can't misroute it onto another file.
  const validateEntry = (entry, label = "") =>
    new Promise((resolve) => {
      addLog(`🔍 ${label}Validating ${entry.file.name} against SOP…`, "info");

      const fail = (sopErrors, reason = "") => {
        updateFileEntry(entry.id, { sopValidated: "fail", sopErrors });
        addLog(
          `❌ ${label}${entry.file.name}: SOP validation failed${reason ? `: ${reason}` : ""}`,
          "error",
        );
        resolve(false);
      };

      dispatch(
        docxValidate(
          entry.file,
          (res) => {
            if (!res?.data?.ok) {
              const errorIssues = (res?.data?.issues || []).filter(
                (issue) => issue.severity === "error",
              );
              fail(
                errorIssues.length > 0
                  ? errorIssues
                  : [{ message: "Document failed SOP validation.", fix: "" }],
              );
              return;
            }

            updateFileEntry(entry.id, { sopValidated: "pass", sopErrors: [] });
            addLog(`✅ ${label}${entry.file.name}: SOP validation passed`, "success");
            resolve(true);
          },
          (err) => {
            const msg = err?.msg || err?.data?.msg || "";
            fail(
              [{ message: msg || "Document failed SOP validation", fix: "" }],
              msg,
            );
          },
        ),
      );
    });

  // Validate button: with several files, validates every docx file one by
  // one; otherwise just the selected file. On failure the first failed file
  // is selected so its SOP errors show in the Preview panel.
  const validateDocument = async () => {
    if (validatingDocx) return;
    const targets = isMultiFile
      ? files.filter((e) => e.fileType === "docx")
      : selectedEntry?.fileType === "docx"
        ? [selectedEntry]
        : [];
    if (!targets.length) return;

    setValidatingDocx(true);
    let firstFailed = null;
    let failCount = 0;
    for (let i = 0; i < targets.length; i++) {
      const label = targets.length > 1 ? `[${i + 1}/${targets.length}] ` : "";
      const ok = await validateEntry(targets[i], label);
      if (!ok) {
        failCount++;
        if (!firstFailed) firstFailed = targets[i];
      }
    }
    setValidatingDocx(false);

    if (targets.length > 1) {
      addLog(
        failCount
          ? `🏁 Validation done — ${failCount}/${targets.length} files failed SOP (see ❌ in the file list).`
          : `🏁 Validation done — all ${targets.length} files passed SOP.`,
        failCount ? "error" : "success",
      );
    }
    if (firstFailed) {
      setSelectedId(firstFailed.id);
      setActiveTab("preview");
    }
  };

  // Extracts the given queue entries (all not-yet-extracted files by
  // default). Single-file uploads always re-extract, as before.
  const extractAll = async (targets) => {
    const queue =
      targets ||
      (isMultiFile
        ? files.filter((e) => e.status !== "done")
        : files);
    if (!queue.length) {
      addLog("ℹ️ All files are already extracted.", "info");
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setStage("extracting");
    setLogs([]);

    addLog(
      queue.length > 1
        ? `🚀 Extracting ${queue.length} files — each will be its own exam set`
        : `🚀 Extracting all questions in a single request`,
      "info"
    );

    const system = buildSystemPrompt(
      SYSTEM_PROMPT_TEXT 
    );


    const userPrompt = `
Extract ALL questions.

OUTPUT RULES:
- STRICT JSON ONLY
- First character MUST be {
- Last character MUST be }
- No markdown
- No explanation
- No text before JSON
- No text after JSON

FORMAT:

{
  "questions": [...]
}

Return ALL questions in one response.
`;

    const prompt = buildPrompt(userPrompt);

    const tryOnce = (targetFile) =>
      new Promise((resolve, reject) => {
        if (controller.signal.aborted) {
          reject(
            new DOMException("Aborted", "AbortError")
          );
          return;
        }

        const timer = setTimeout(() => {
          reject(
            new Error(
              `Timeout after ${EXTRACT_TIMEOUT_MS}ms`
            )
          );
        }, EXTRACT_TIMEOUT_MS);

        const onAbort = () => {
          clearTimeout(timer);
          reject(
            new DOMException("Aborted", "AbortError")
          );
        };

        controller.signal.addEventListener(
          "abort",
          onAbort,
          { once: true }
        );

        callAPIPromise(
          targetFile,
          system,
          prompt,
          null,
          "extract"
        )
          .then((result) => {
            clearTimeout(timer);
            controller.signal.removeEventListener(
              "abort",
              onAbort
            );
            resolve(result);
          })
          .catch((err) => {
            clearTimeout(timer);
            controller.signal.removeEventListener(
              "abort",
              onAbort
            );
            reject(err);
          });
      });

    // Extracts one file and returns its questions deduped and sorted by
    // their in-file order.
    const extractFile = async (targetFile) => {
      const { text } = await tryOnce(targetFile);

      const first = text.indexOf("{");
      const last = text.lastIndexOf("}");

      if (first === -1 || last === -1) {
        throw new Error("No JSON found in response");
      }

      const parsed = safeParseJSON(
        text.slice(first, last + 1)
      );

      if (!parsed?.questions?.length) {
        throw new Error("No questions returned");
      }

      const seen = new Set();

      return parsed.questions
        .filter((q) => {
          if (seen.has(q.order)) return false;
          seen.add(q.order);
          return true;
        })
        .sort((a, b) => a.order - b.order);
    };

    // Maps each question to its section/part using the pattern scaled to
    // this file's own question count (every file becomes its own exam set).
    const assignSections = (fileQuestions, setLabel) => {
      const sectionData = questionTypes?.sectionData || [];
      if (!sectionData.length) {
        addLog(
          `⚠️ ${setLabel}No question pattern selected — sections not assigned`,
          "warn"
        );
        return fileQuestions;
      }

      // Section-wise (model set) patterns keep their own counts, but the
      // raw sectionData doesn't reliably carry questionFrom/questionTo, so
      // rebuild the ranges at the pattern's own total (ratio 1). A blank
      // pattern (all zero) falls back to this file's question count.
      const patternTotal = sectionData.reduce(
        (sum, sec) => sum + (Number(sec.totalQuestions) || 0),
        0
      );
      const currentPattern = scalePattern(
        sectionData,
        isSectionWiseActive && patternTotal
          ? patternTotal
          : fileQuestions.length
      );

      const unassigned = [];
      const mapped = fileQuestions.map((q) => {
        for (const section of currentPattern) {
          for (const part of section.parts || []) {
            if (
              q.order >= part.questionFrom &&
              q.order <= part.questionTo
            ) {
              return {
                ...q,
                section: {
                  examPatternId,
                  sectionId: section._id,
                  partId: part._id,
                },
                marks: part.correct,
                negativeMarks: part.negative,
              };
            }
          }
        }

        unassigned.push(q.order);
        return q;
      });

      // One summary line instead of a warning per question.
      if (unassigned.length) {
        addLog(
          `⚠️ ${setLabel}${unassigned.length} question(s) outside the pattern's range — section not assigned (Q${unassigned.slice(0, 10).join(", Q")}${unassigned.length > 10 ? "…" : ""})`,
          "warn"
        );
      }
      return mapped;
    };

    // Files run one after another (not in parallel) so the backend isn't
    // flooded and Cancel stops before the next file starts. Each file's
    // questions are stored on its own queue entry — never merged — so a
    // failed file doesn't affect the others.
    let doneCount = 0;

    for (let i = 0; i < queue.length; i++) {
      if (controller.signal.aborted) break;

      const entry = queue[i];
      const label = queue.length > 1 ? `[${i + 1}/${queue.length}] ` : "";
      setSelectedId(entry.id);
      updateFileEntry(entry.id, {
        status: "extracting",
        questions: [],
        saved: false,
      });
      addLog(`📄 ${label}Extracting ${entry.file.name}...`, "info");

      try {
        const fileQuestions = await extractFile(entry.file);
        const withImages = replacePlaceholders(
          assignSections(fileQuestions, label),
          imageMapRef.current
        );

        updateFileEntry(entry.id, {
          status: "done",
          questions: withImages,
          manualTotal: String(withImages.length),
        });
        // totalQuestions still feeds the system prompt's pattern hint.
        if (!isMultiFile) setTotalQuestions(withImages.length);
        doneCount++;

        addLog(
          `✅ ${label}${entry.file.name}: ${withImages.length} questions extracted`,
          "success"
        );
        setActiveTab("questions");
      } catch (e) {
        if (controller.signal.aborted) {
          // Cancelled by the user — not a failure; it can be extracted again.
          updateFileEntry(entry.id, { status: "pending" });
          break;
        }
        updateFileEntry(entry.id, { status: "failed" });
        addLog(
          `❌ ${label}${entry.file.name}: Extraction failed: ${e.message}`,
          "error"
        );
      }
    }

    if (queue.length > 1) {
      addLog(
        `🏁 Done. ${doneCount}/${queue.length} files extracted — review and save each file as its own exam set.`,
        "success"
      );
    }
    setStage("done");
  };

  // Typeset math in the Questions panel only when what it shows changes
  // (tab opened, another file selected, new questions) — NOT on every
  // render. Scoped to the panel and chained so MathJax runs never overlap;
  // re-scanning the whole page on each render hung the page with
  // multi-file extractions.
  const questionsPanelRef = useRef(null);
  const typesetChainRef = useRef(Promise.resolve());
  useEffect(() => {
    if (activeTab !== "questions" || !questions.length) return;
    const timer = setTimeout(() => {
      const el = questionsPanelRef.current;
      const mathJax = window.MathJax;
      if (!el || !mathJax?.typesetPromise) return;
      typesetChainRef.current = typesetChainRef.current
        .then(() => mathJax.typesetPromise([el]))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
    // `questions` keeps its reference until that file is re-extracted.
  }, [activeTab, selectedEntry?.id, questions]);

  const copyJSON = () =>
    navigator.clipboard.writeText(
      JSON.stringify({ totalExtracted: questions.length, questions }, null, 2),
    );

  // ── Styles ────────────────────────────────────────────────────────────────

  const s = {
    page: {
      fontFamily: "Inter,sans-serif",
      maxWidth: 1000,
      margin: "0 auto",
      padding: 10,
      background: "#0f172a",
      minHeight: "400px",
      color: "#e2e8f0",
    },
    drop: {
      border: `2px dashed ${dragOver ? "#6366f1" : "#334155"}`,
      borderRadius: 12,
      padding: 32,
      textAlign: "center",
      cursor: "pointer",
      background: dragOver ? "#1e1b4b" : "#1e293b",
      transition: "all 0.2s",
    },
    card: {
      background: "#1e293b",
      borderRadius: 10,
      padding: 16,
      marginTop: 14,
      border: "1px solid #334155",
    },
    btn: (c, dis) => ({
      padding: "9px 18px",
      borderRadius: 8,
      border: "none",
      cursor: dis ? "not-allowed" : "pointer",
      fontWeight: 600,
      fontSize: 13,
      background: dis ? "#1e293b" : c,
      color: dis ? "#475569" : "#fff",
      transition: "all 0.2s",
    }),
    inp: {
      background: "#0f172a",
      border: "1px solid #334155",
      borderRadius: 8,
      padding: "7px 12px",
      color: "#e2e8f0",
      fontSize: 14,
      width: 80,
      textAlign: "center",
    },
    // Small square icon button for file-row actions (extract / remove).
    iconBtn: (col, dis) => ({
      width: 28,
      height: 28,
      padding: 0,
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 6,
      border: `1px solid ${dis ? "#334155" : `${col}66`}`,
      background: dis ? "transparent" : `${col}1f`,
      color: dis ? "#475569" : col,
      cursor: dis ? "not-allowed" : "pointer",
      transition: "all 0.2s",
    }),
    // Compact input used on file rows (overrides global input sizing).
    rowInp: {
      height: 30,
      margin: 0,
      padding: "0 10px",
      background: "#0f172a",
      border: "1px solid #334155",
      borderRadius: 6,
      color: "#e2e8f0",
      fontSize: 12,
      lineHeight: "30px",
      boxShadow: "none",
      outline: "none",
      boxSizing: "border-box",
    },
    rowLabel: {
      fontSize: 11,
      color: "#64748b",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      whiteSpace: "nowrap",
    },
    badge: (bg, col) => ({
      fontSize: 11,
      padding: "2px 7px",
      borderRadius: 99,
      fontWeight: 600,
      background: bg,
      color: col,
    }),
    tab: (a) => ({
      padding: "8px 14px",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 500,
      borderRadius: "6px 6px 0 0",
      border: "none",
      background: a ? "#6366f1" : "transparent",
      color: a ? "#fff" : "#94a3b8",
    }),
    opt: (ok) => ({
      display: "flex",
      gap: 8,
      padding: "6px 10px",
      borderRadius: 6,
      marginTop: 4,
      background: ok ? "#064e3b" : "#0f172a",
      border: `1px solid ${ok ? "#10b981" : "#1e293b"}`,
      alignItems: "flex-start",
    }),
    logBox: {
      background: "#0f172a",
      borderRadius: 8,
      padding: 10,
      maxHeight: 180,
      overflowY: "auto",
      fontFamily: "monospace",
      fontSize: 11,
      marginTop: 8,
    },
    logColors: {
      info: "#94a3b8",
      success: "#34d399",
      warn: "#fbbf24",
      error: "#f87171",
    },
  };

  /***********************Scale pattern base on total question******************************** */

  function scalePattern(pattern, newTotal) {
    const origTotal = pattern.reduce((s, sec) => s + sec.totalQuestions, 0);
    const ratio = newTotal / (origTotal || newTotal);

    // ✅ Check if all sections have 0 questions (blank pattern)
    const isBlankPattern = origTotal === 0;

    // Count total parts across all sections for even distribution
    const totalParts = pattern.reduce((s, sec) => s + sec.parts.length, 0);

    let scaledSections;

    if (isBlankPattern) {
      // ✅ Evenly distribute newTotal across sections by their part count
      let remaining = newTotal;
      scaledSections = pattern.map((sec, secIdx) => {
        const isLastSection = secIdx === pattern.length - 1;
        const secShare = isLastSection
          ? remaining
          : Math.floor((sec.parts.length / totalParts) * newTotal);
        remaining -= secShare;
        return { ...sec, scaledTotal: secShare, remainder: 0 };
      });
    } else {
      scaledSections = pattern.map((sec) => ({
        ...sec,
        scaledTotal: Math.floor(sec.totalQuestions * ratio),
        remainder: (sec.totalQuestions * ratio) % 1,
      }));

      // Distribute leftover questions due to flooring
      let distributed =
        newTotal - scaledSections.reduce((s, sec) => s + sec.scaledTotal, 0);
      scaledSections = scaledSections
        .map((sec, i) => ({ ...sec, i }))
        .sort((a, b) => b.remainder - a.remainder)
        .map((sec, idx) => ({
          ...sec,
          scaledTotal: sec.scaledTotal + (idx < distributed ? 1 : 0),
        }))
        .sort((a, b) => a.i - b.i);
    }

    let cursor = 1;

    return scaledSections.map((sec) => {
      const secTotal = sec.scaledTotal;
      const origSecTotal = sec.totalQuestions;
      const secRatio = secTotal / (origSecTotal || secTotal);

      // ✅ Check if all parts in this section have 0 questions
      const isBlankSection = origSecTotal === 0;

      let scaledParts;

      if (isBlankSection) {
        // ✅ Evenly distribute secTotal across parts
        let remainingParts = secTotal;
        scaledParts = sec.parts.map((p, pIdx) => {
          const isLastPart = pIdx === sec.parts.length - 1;
          const partShare = isLastPart
            ? remainingParts
            : Math.floor(secTotal / sec.parts.length);
          remainingParts -= partShare;
          return { ...p, scaledQ: partShare, remainder: 0 };
        });
      } else {
        scaledParts = sec.parts.map((p) => ({
          ...p,
          scaledQ: Math.floor(p.totalQuestions * secRatio),
          remainder: (p.totalQuestions * secRatio) % 1,
        }));

        let partLeftover =
          secTotal - scaledParts.reduce((s, p) => s + p.scaledQ, 0);
        scaledParts = scaledParts
          .map((p, i) => ({ ...p, i }))
          .sort((a, b) => b.remainder - a.remainder)
          .map((p, idx) => ({
            ...p,
            scaledQ: p.scaledQ + (idx < partLeftover ? 1 : 0),
          }))
          .sort((a, b) => a.i - b.i);
      }

      const newParts = scaledParts.map((p) => {
        const from = cursor;
        const to =
          cursor +
          (typeof p.scaledQ === "string"
            ? parseInt(p.scaledQ, 10)
            : p.scaledQ) -
          1;
        cursor += p.scaledQ;
        return {
          questionType: p.questionType,
          questionFrom: from,
          questionTo: to,
          totalQuestions: p.scaledQ,
          correct: p.correct,
          negative: p.negative,
          _id: p._id,
          questionTypeName: p.questionTypeName,
        };
      });

      return {
        isCompulsory: sec.isCompulsory,
        name: sec.name,
        totalQuestions: secTotal,
        maxAttempt: secTotal,
        parts: newParts,
        _id: sec._id,
      };
    });
  }

  /**********************Save Data************************* */

  // saveAll = true → every extracted, not-yet-saved file (Save All);
  // otherwise just the selected file. The course/part popup (when the
  // template needs one) is shown once and applies to all chosen files.
  const onHandleSaveData = (saveAll = false) => {
    if (isSaving) return;
    let targets;
    if (saveAll) {
      targets = files.filter(
        (e) => e.status === "done" && e.questions.length && !e.saved,
      );
      if (!targets.length) {
        dispatch(showErrorSnackbar("No extracted, unsaved files to save."));
        return;
      }
    } else {
      if (!selectedEntry?.questions?.length) {
        dispatch(showErrorSnackbar("Nothing to save — extract this file first."));
        return;
      }
      if (isMultiFile && selectedEntry.saved) {
        dispatch(showErrorSnackbar("This file is already saved as an exam set."));
        return;
      }
      targets = [selectedEntry];
    }
    saveTargetIdsRef.current = targets.map((e) => e.id);
    if ([2, 9 ,12].includes(activeTemplate?.id)) {
      saveExamset();
    } else {
      setIsOpen(true);
    }
  };

  // Builds the payload for one exam set; saveExamset calls it once per file.
  const buildExamSet = (courseID, partNo, partType, setQuestions, setTotal, setName) => {
    const scaledPattern = scalePattern(
      questionTypes?.sectionData || [],
      setTotal,
    );

    const totalMarks =
      scaledPattern?.reduce(
        (total, section) =>
          total +
          section.parts.reduce(
            (partTotal, part) =>
              partTotal +
              (part.maxAttempt ? part.maxAttempt : part.totalQuestions) *
              part.correct,
            0,
          ),
        0,
      ) ?? 0;

    const isPracticeTest = activeTemplate?.id === 3 || activeTemplate?.id === 5 || activeTemplate?.id === 13;
    const isPYQ =
      activeTemplate?.id === 4 ||
      activeTemplate?.id === 6 ||
      activeTemplate?.id === 11;
    const duration = isSectionWiseActive
      ? setTotal
      : setTotal * 1.5;
    const totalMarksVal = isSectionWiseActive
      ? questionTypes?.totalMarks
      : totalMarks;
    const examPatternId = questionTypes?._id;

    // ── Build the shared exam fields as a plain object (never stringify+parse) ──
    const sharedExamFields = {
      examType: activeTemplate?.type,
      isFreeUser: activeTemplate?.id === 12 || activeTemplate?.id === 8 || activeTemplate?.id === 9,
      isPracticeTest,
      isPYQ,
      isSectionWise: isSectionWiseActive,
      getAdmitCard: false,
      isActive: true,
      examDate: moment().format(),
      examPatternId,
      createdByAI: true,
      duration,
      totalQuestions: setTotal,
      totalAddedQuestion: setTotal,
      name: setName,
      totalMarks: totalMarksVal,
      QuestionData: setQuestions,
      sections: scaledPattern, // ✅ raw array — no serialization needed
    };
    if ([2, 5, 6, 9].includes(activeTemplate?.id)) {
      sharedExamFields.topicId = topicName?._id;
      sharedExamFields.subTopicId = subTopicName?._id;
      // sharedExamFields.subjectId = tempSubject[0]?._id;
    }
    if ([13].includes(activeTemplate?.id)) {
      sharedExamFields.topicId = topicName?._id;
    }
    if (![1, 11 ,12].includes(activeTemplate?.id)) {
      sharedExamFields.subjectId = tempSubject[0]?._id;
    }
    if (activeTemplate?.id === 11) {
      sharedExamFields.examMonth = month?.value;
      sharedExamFields.examYear = year?.value;
    }

    // 1 = Mock Test — its Submit popup also collects Part Type / Part.
    if ([1, 7, 3, 4, 8, 5, 6,13].includes(activeTemplate?.id)) {
      sharedExamFields.examPartId = partType;
      sharedExamFields.partNo = partNo;
      sharedExamFields.isPart = true;
    } else {
      sharedExamFields.isPart = false;
    }

    // ── Build the final payload object directly ────────────────────────────────
    let parsed;

    if (isSectionWiseActive) {
      // template1Structure
      parsed = [
        {
          baseCourseId: examStream?._id,
          examTypeId: exam?._id,
          courseId: courseID,
          ...(subExam?._id && { subExamTypeId: subExam._id }),
          ...sharedExamFields,
        },
      ];
    } else {
      // otherTemplateStructure
      parsed = [
        {
          examsArray: [
            {
              baseCourseId: examStream?._id,
              examTypeId: exam?._id,
              courseId: courseID,
              ...(subExam?._id && { subExamTypeId: subExam._id }),
            },
          ],
          ...sharedExamFields,
        },
      ];
    }

    let cleaned = parsed?.map((item) => ({
      ...item,
      ...(!isSectionWiseActive && item?.examsArray
        ? {
          examsArray: item.examsArray.map((exam) => {
            if (
              !exam?.courseId ||
              exam.courseId.length === 0 ||
              exam.courseId.every((val) => val == null)
            ) {
              const { courseId, ...rest } = exam;
              return rest;
            }

            return exam;
          }),
        }
        : {}),
    }));
    return cleaned[0];
  };

  // Saves each target file's questions as its own exam set (per-file save,
  // same as pyqExtraction). Several targets (Save All) are saved one by one;
  // a failed save stops the rest so they can be retried. A single upload
  // keeps the old behaviour (manual "Total Qs" override + the Exam Set Name
  // field, falling back to the file name); with multiple files each set
  // uses the name and count edited on its own file row.
  const saveExamset = (courseID, subjectID, partNo, partType) => {
    const targets = saveTargetIdsRef.current
      .map((id) => files.find((e) => e.id === id))
      .filter((e) => e?.questions?.length);
    if (!targets.length) return;

    const examSets = targets.map((target) => {
      const fileSetName =
        target.examSetName?.trim() || fileNameToSetName(target.file.name);
      const setName = isMultiFile
        ? fileSetName
        : examSetName?.trim() || fileSetName;

      return {
        target,
        setName,
        examSet: buildExamSet(
          courseID,
          partNo,
          partType,
          target.questions,
          // Always the file's own count — the global totalQuestions can be
          // stale after switching between multi- and single-file uploads.
          parseInt(target.manualTotal, 10) || target.questions.length,
          setName,
        ),
      };
    });

    setIsSaving(true);
    const saveNext = (i) => {
      if (i >= examSets.length) {
        setIsSaving(false);
        if (examSets.length > 1) {
          addLog(`🏁 All ${examSets.length} exam sets saved.`, "success");
        }
        return;
      }
      const { target, setName, examSet } = examSets[i];
      const label = examSets.length > 1 ? `[${i + 1}/${examSets.length}] ` : "";
      addLog(`💾 ${label}Saving "${setName}"...`, "info");
      dispatch(
        addExamSetWithQuestions(
          { examSetData: [examSet] },
          () => {
            updateFileEntry(target.id, { saved: true });
            addLog(
              `✅ ${label}${target.file.name}: saved as exam set "${setName}"`,
              "success",
            );
            saveNext(i + 1);
          },
          () => {
            setIsSaving(false);
            addLog(
              `❌ ${label}${target.file.name}: save failed${examSets.length - i > 1 ? " — remaining files not saved, use Save All to retry" : ""}`,
              "error",
            );
          },
        ),
      );
    };
    saveNext(0);

    setAiOutput(
      JSON.stringify(
        examSets.map((e) => e.examSet),
        null,
        2,
      ),
    );
  };

  const handleDelet = (value) => {
    setSelectedSubjectList((prev) =>
      prev.map((item) =>
        item._id === value ? { ...item, isSelected: false } : item,
      ),
    );
  };

  const onHandleTopic = (subject) => {
    setSubjectName(subject);
    const examObj = {
      baseCourseId: examStream?._id,
      examTypeId: exam?._id,
      subjectId: subject?._id,
    };
    if (subExam?._id) examObj.subExamTypeId = subExam?._id;
    dispatch(
      getTopicList(
        examObj,
        (res) => {
          setTopicList(res.data);
        },
        () => { },
      ),
    );
  };

  const onChangeTopic = (value) => {
    setTopicName(value);
    setSubTopicList([]);
    dispatch(
      getSubTopicList(
        {
          baseCourseId: examStream?._id,
          examTypeId: exam?._id,
          subjectId: tempSubject[0]?._id,
          topicId: value._id,
        },
        (res) => {
          setSubTopicList(res.data);
        },
        () => { },
      ),
    );
  };
  return (
    <>
      <div className="contentBox">
        <PageTitle name={"Exam Extractor"} />
        <div className="formDiv contentScrollbar">
          <div className="ai-command-center-container">
            {/* Main Content */}
            <div className="command-content row mx-0">
              {/* Left Section */}
              <div className="col-md-6" style={{ height: "calc(100vh - 100px)", overflowY: "auto" }}>
                {/* Quick Command Templates */}
                <div className="quick-templates-card">
                  <div className="card-header-Ai">
                    <Zap size={18} className="header-icon" />
                    <h2>Quick Command Templates</h2>
                  </div>
                  <div className="p-3">
                    <div className="form-outer-AI">
                      {/* Exam Selection */}
                      <div className="form-field-AI">
                        <label htmlFor="exam">EXAM *</label>
                        <Dropdown
                          labelName="Exam Name"
                          setInputText={(value) => onChangeExam(value)}
                          options={examList}
                          value={exam}
                        />
                      </div>

                      {/* Sub Exam Selection */}
                      {subExamList?.length > 0 ? (
                        <div className="form-field-AI">
                          <label htmlFor="sub exam">SUB EXAM *</label>
                          <Dropdown
                            labelName="Sub Exam"
                            setInputText={(value) => onChangeSubExam(value)}
                            options={subExamList}
                            value={subExam}
                          />
                        </div>
                      ) : null}

                      {/* Exam Stream Selection */}
                      <div className="form-field-AI">
                        <label htmlFor="exam stream">EXAM STREAM *</label>
                        <Dropdown
                          labelName="Exam Stream"
                          setInputText={(value) => onChangeExamStream(value)}
                          options={examStreamList}
                          value={examStream}
                        />
                      </div>
                    </div>
                    <div className="templates-grid my-2">
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          className={`template-chip ${activeTemplate?.name === template.name
                            ? "active"
                            : ""
                            }`}
                          onClick={() => handleTemplateClick(template)}
                          style={{
                            "--chip-color": template.color,
                            border: `1px solid ${template.color}`,
                            // color: "#000000",
                          }}
                          title={template.name}
                        >
                          <span className="template-icon">{template.icon}</span>
                          <span className="template-name">{template.name}</span>
                        </button>
                      ))}
                    </div>
                    <div className="row">
                      {activeTemplate?.id === 2 ||
                        activeTemplate?.id === 7 ||
                        activeTemplate?.id === 3 ||
                        activeTemplate?.id === 4 ||
                        activeTemplate?.id === 8 ||
                        activeTemplate?.id === 5 ||
                        activeTemplate?.id === 6 ||
                        activeTemplate?.id === 9 ||
                        activeTemplate?.id === 10 ||
                        activeTemplate?.id === 13 ? (
                        <div className="d-flex my-2 align-items-center">
                          <LabelField labelName={"Subject *"} />
                          <div className=" ">
                            <div className="d-flex row justify-content-between ">
                              {selectedSubjectList
                                ?.filter((exam) => exam?.isSelected)
                                .map((exam) => (
                                  <div
                                    className="col-auto d-flex align-items-center justify-content-between rounded-pill border mx-1 py-1 me-2 mb-2 cursor"
                                    key={exam._id}
                                  >
                                    <p className="col-auto mb-0 font-11">
                                      {exam.name}
                                    </p>
                                    <div
                                      className="d-flex justify-content-end"
                                      onClick={() => handleDelet(exam._id)}
                                    >
                                      <span className="mx-2 text-primary text-end">
                                        x
                                      </span>
                                    </div>
                                  </div>
                                ))}
                            </div>

                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm  "
                              onClick={() => {
                                setExamOpen(true);
                              }}
                            >
                              <RemoveRedEyeIcon
                                fontSize="small"
                                className="me-2"
                              />
                              View Subjects
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="row">
                      {activeTemplate?.id === 2 ||
                        activeTemplate?.id === 5 ||
                        activeTemplate?.id === 6 ||
                        activeTemplate?.id === 9 ||
                        activeTemplate?.id === 13 ? (
                        <div className="form-field-AI col-6">
                          <label htmlFor="exam">TOPIC *</label>
                          <Dropdown
                            labelName="Topic Name"
                            setInputText={(value) => onChangeTopic(value)}
                            options={topicList}
                            value={topicName}
                          />
                        </div>
                      ) : null}
                      {activeTemplate?.id === 2 ||
                        activeTemplate?.id === 5 ||
                        activeTemplate?.id === 6 ||
                        activeTemplate?.id === 9 ? (
                        <div className="form-field-AI col-6">
                          <label htmlFor="exam">SUB TOPIC *</label>
                          <Dropdown
                            labelName="Sub Topic Name"
                            setInputText={(value) => setSubTopicName(value)}
                            options={subTopicList}
                            value={subTopicName}
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className="form-outer-AI">
                      {/* {activeTemplate?.name !== "Mock Test" && (
                        <> */}
                      <div className="form-field-AI">
                        <label htmlFor="Question Types">QUESTION PATTERN</label>
                        <Dropdown
                          labelName="Question Types"
                          setInputText={(value) =>
                            onChangeQuestionPattern(value)
                          }
                          options={patternList}
                          value={questionTypes}
                        />
                      </div>
                      {/* </>
                      )} */}
                      <div className="form-field-AI">
                        <label>Exam Set Name</label>
                        {isMultiFile ? (
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            Multiple files — set each exam set's name on its
                            file row below.
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={examSetName}
                            onChange={(e) => {
                              const value = e.target.value;
                              setExamSetName(value);
                            }}
                            className="param-input"
                            placeholder={
                              selectedEntry?.examSetName || "ENTER EXAM SET NAME"
                            }
                            maxLength={100}
                          />
                        )}
                      </div>
                      {activeTemplate?.id === 7 ? (
                        <div className="form-field-AI">
                          <label>Model Set</label>
                          <ToggleSwitchBtn
                            // labelName={"Is This Private Blog"}
                            getToggleval={(value) => changeToggle(value)}
                            isActive={isSectionWiseActive}
                            disabled={!questionTypes}
                          // Isrequired={true}
                          />
                        </div>
                      ) : null}
                    </div>
                    {activeTemplate?.id === 11 && (
                      <div className="row">
                        {/* Exam Selection */}
                        <div className="col-md-6 form-group">
                          <label htmlFor="exam">MONTH *</label>
                          <Dropdown
                            labelName="Select Month"
                            setInputText={(value) => setMonth(value)}
                            options={monthData}
                            value={month}
                          />
                        </div>

                        {/* Exam Stream Selection */}
                        <div className="col-md-6 form-group">
                          <label htmlFor="exam stream">YEAR *</label>
                          <Dropdown
                            labelName="Select Year"
                            setInputText={(value) => setYear(value)}
                            options={yearData1}
                            value={year}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* AI Command Input */}
                <div className="ai-command-card position-relative mt-3">
                  <div className="card-header-Ai">
                    <div className="header-left">
                      <Wand2 size={18} className="header-icon" />
                      <h2>AI Command</h2>
                    </div>
                    <button className="knowledge-base-btn">
                      Uses Knowledge Base
                    </button>
                  </div>

                  <div className="command-input-section p-1">
                    <div style={s.page}>
                      {/* Drop Zone */}
                      <div
                        style={s.drop}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                          if (stage !== "extracting") {
                            handleFiles(e.dataTransfer.files);
                          }
                        }}
                        onClick={() =>
                          stage !== "extracting" && fileRef.current.click()
                        }
                      >
                        <div style={{ fontSize: 30 }}>
                          {files.length ? "🗂️" : "📂"}
                        </div>
                        <p
                          style={{
                            color: "#94a3b8",
                            fontSize: 13,
                            margin: "6px 0 2px",
                          }}
                        >
                          {files.length
                            ? `${files.length} file${files.length > 1 ? "s" : ""} added · click or drop to add more`
                            : "Drag & drop or click to upload (multiple allowed)"}
                        </p>
                        <p
                          style={{ color: "#475569", fontSize: 11, margin: 0 }}
                        >
                          JPG · PNG · PDF · DOC · DOCX
                        </p>
                        <input
                          ref={fileRef}
                          type="file"
                          multiple
                          accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            handleFiles(e.target.files);
                            // Reset so re-picking the same file fires onChange.
                            e.target.value = "";
                          }}
                        />
                      </div>

                      {/* File queue — each file is extracted on its own and
                          saved as its own exam set (never merged). Click a
                          file to review its questions and save it. */}
                      {files.length > 0 && (
                        <div style={{ ...s.card, padding: 10 }}>
                          {isMultiFile && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                                margin: "0 4px 8px",
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Each file is a separate exam set — select a
                                file to review its questions, then Save Data.
                              </span>
                              <button
                                type="button"
                                title="Remove all files"
                                disabled={stage === "extracting"}
                                style={{
                                  ...s.iconBtn(
                                    "#f87171",
                                    stage === "extracting",
                                  ),
                                  width: "auto",
                                  padding: "0 10px",
                                  gap: 6,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                                onClick={removeAllFiles}
                              >
                                <Trash2 size={14} /> Remove All
                              </button>
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                            }}
                          >
                          {files.map((entry, idx) => {
                            const locked = stage === "extracting";
                            const isActive = entry.id === selectedEntry?.id;
                            const statusBadge = {
                              pending: ["#334155", "#cbd5e1", "Pending"],
                              extracting: ["#1e3a5f", "#60a5fa", "Extracting…"],
                              done: [
                                "#064e3b",
                                "#34d399",
                                `${entry.questions.length} Qs`,
                              ],
                              failed: ["#4c0519", "#f87171", "Failed"],
                            }[entry.status];
                            return (
                              <div
                                key={entry.id}
                                onClick={() => selectFile(entry.id)}
                                style={{
                                  padding: "8px 10px",
                                  borderRadius: 8,
                                  cursor: "pointer",
                                  background: isActive ? "#0f172a" : "transparent",
                                  border: `1px solid ${isActive ? "#6366f1" : "#334155"}`,
                                  transition: "all 0.2s",
                                }}
                              >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 12,
                                    color: "#64748b",
                                    minWidth: 16,
                                  }}
                                >
                                  {idx + 1}.
                                </span>
                                <span style={{ fontSize: 14 }}>
                                  {entry.fileType === "image"
                                    ? "🖼️"
                                    : entry.fileType === "pdf"
                                      ? "📕"
                                      : "📄"}
                                </span>
                                <span
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: 13,
                                    color: "#e2e8f0",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={entry.file.name}
                                >
                                  {entry.file.name}
                                </span>
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "#64748b",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {(entry.file.size / 1024).toFixed(1)} KB
                                </span>
                                {entry.sopValidated === "pass" && (
                                  <span
                                    title="SOP OK"
                                    style={{
                                      ...s.badge("#064e3b", "#34d399"),
                                      fontSize: 10,
                                    }}
                                  >
                                    SOP ✓
                                  </span>
                                )}
                                {entry.sopValidated === "fail" && (
                                  <span
                                    title="SOP Failed"
                                    style={{
                                      ...s.badge("#4c0519", "#f87171"),
                                      fontSize: 10,
                                    }}
                                  >
                                    SOP ✗
                                  </span>
                                )}
                                {statusBadge && (
                                  <span
                                    style={{
                                      ...s.badge(statusBadge[0], statusBadge[1]),
                                      fontSize: 10,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {statusBadge[2]}
                                  </span>
                                )}
                                {entry.saved && (
                                  <span
                                    style={{
                                      ...s.badge("#064e3b", "#34d399"),
                                      fontSize: 10,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Saved
                                  </span>
                                )}
                                {/* Extract just this file (▶ first time,
                                    ↻ to re-extract). */}
                                {isMultiFile && (
                                  <button
                                    type="button"
                                    title={
                                      entry.status === "pending"
                                        ? "Extract this file"
                                        : "Re-extract this file"
                                    }
                                    disabled={locked}
                                    style={s.iconBtn("#818cf8", locked)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Re-extracting clears "Saved", so saving
                                      // again would create a duplicate set.
                                      if (
                                        entry.saved &&
                                        !window.confirm(
                                          `"${entry.examSetName}" is already saved. Re-extracting allows saving it again as a NEW exam set. Continue?`,
                                        )
                                      )
                                        return;
                                      extractAll([entry]);
                                    }}
                                  >
                                    {entry.status === "pending" ? (
                                      <Play size={14} />
                                    ) : (
                                      <RotateCw size={14} />
                                    )}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  title="Remove"
                                  disabled={locked}
                                  style={s.iconBtn("#f87171", locked)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFile(entry.id);
                                  }}
                                >
                                  <X size={14} />
                                </button>
                              </div>

                              {/* Per-file exam set name + question count
                                  (used when this file is saved). */}
                              {isMultiFile && (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginTop: 8,
                                    paddingLeft: 24,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span style={s.rowLabel}>Exam Set Name</span>
                                  <input
                                    type="text"
                                    style={{
                                      ...s.rowInp,
                                      flex: 1,
                                      minWidth: 160,
                                      opacity: entry.saved ? 0.6 : 1,
                                    }}
                                    value={entry.examSetName}
                                    placeholder="Exam set name"
                                    maxLength={100}
                                    disabled={entry.saved}
                                    onFocus={() => selectFile(entry.id)}
                                    onChange={(e) =>
                                      updateFileEntry(entry.id, {
                                        examSetName: e.target.value,
                                      })
                                    }
                                  />
                                  <span style={s.rowLabel}>Total Qs</span>
                                  <input
                                    type="number"
                                    style={{
                                      ...s.rowInp,
                                      width: 64,
                                      textAlign: "center",
                                      opacity: entry.saved ? 0.6 : 1,
                                    }}
                                    min={1}
                                    max={500}
                                    value={entry.manualTotal}
                                    placeholder={
                                      entry.questions.length
                                        ? String(entry.questions.length)
                                        : "auto"
                                    }
                                    disabled={entry.saved}
                                    onFocus={() => selectFile(entry.id)}
                                    onChange={(e) =>
                                      updateFileEntry(entry.id, {
                                        manualTotal: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                              )}
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      )}

                      {/* Controls */}
                      {file && (
                        <div
                          style={{
                            ...s.card,
                            display: "flex",
                            gap: 10,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {/* Manual override applies to a single set only;
                                with multiple files each set uses its own count. */}
                            {files.length <= 1 && (
                              <>
                                <span style={{ fontSize: 13, color: "#94a3b8" }}>
                                  Total Qs:
                                </span>
                                <input
                                  style={s.inp}
                                  type="number"
                                  min={1}
                                  max={500}
                                  value={selectedEntry?.manualTotal ?? ""}
                                  onChange={(e) => {
                                    updateFileEntry(selectedEntry.id, {
                                      manualTotal: e.target.value,
                                    });
                                    setTotalQuestions(e.target.value);
                                  }}
                                  placeholder="e.g. 145"
                                />
                              </>
                            )}
                            {questions.length > 0 && (
                              <span
                                style={{
                                  ...s.badge("#1e3a5f", "#60a5fa"),
                                  fontSize: 11,
                                }}
                              >
                                Detected: {questions.length}
                              </span>
                            )}
                          </div>

                          {turnsUsed > 0 && (
                            <span
                              style={{
                                ...s.badge("#1e3a5f", "#818cf8"),
                                fontSize: 11,
                              }}
                            >
                              Last run: {turnsUsed} turn
                              {turnsUsed > 1 ? "s" : ""}
                            </span>
                          )}

                          {sopValidated === "pass" && (
                            <span
                              style={{
                                ...s.badge("#064e3b", "#34d399"),
                                fontSize: 11,
                              }}
                            >
                              ✅ SOP OK
                            </span>
                          )}
                          {sopValidated === "fail" && (
                            <span
                              style={{
                                ...s.badge("#4c0519", "#f87171"),
                                fontSize: 11,
                              }}
                            >
                              ❌ SOP Failed
                            </span>
                          )}

                          <div
                            style={{
                              marginLeft: "auto",
                              display: "flex",
                              gap: 8,
                            }}
                          >
                            {/* Manual SOP validation — docx only. With several
                                files it validates every docx file. */}
                            {(isMultiFile
                              ? files.some((e) => e.fileType === "docx")
                              : fileType === "docx") && (
                              <button
                                style={s.btn(
                                  "#0891b2",
                                  validatingDocx || stage === "extracting",
                                )}
                                disabled={
                                  validatingDocx || stage === "extracting"
                                }
                                onClick={validateDocument}
                              >
                                {validatingDocx
                                  ? "🔍 Validating..."
                                  : isMultiFile
                                    ? `🔍 Validate All (${files.filter((e) => e.fileType === "docx").length})`
                                    : "🔍 Validate"}
                              </button>
                            )}
                            {/* FIX: Cancel button visible during extraction */}
                            {stage === "extracting" && (
                              <button
                                style={s.btn("#ef4444", false)}
                                onClick={cancelExtraction}
                              >
                                🛑 Cancel
                              </button>
                            )}
                            <button
                              style={s.btn("#6366f1", stage === "extracting")}
                              disabled={stage === "extracting"}
                              onClick={() => extractAll()}
                            >
                              {stage === "extracting"
                                ? "⏳ Extracting..."
                                : isMultiFile
                                  ? "🚀 Extract All Files"
                                  : "🚀 Extract All Questions"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Spinner while extracting */}
                      {stage === "extracting" && (
                        <div
                          style={{
                            marginTop: 12,
                            padding: "12px 16px",
                            background: "#1e293b",
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              width: 18,
                              height: 18,
                              border: "3px solid #334155",
                              borderTop: "3px solid #6366f1",
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                            }}
                          />
                          <span style={{ fontSize: 13, color: "#94a3b8" }}>
                            Extracting Content — check logs
                            below for progress...
                          </span>
                          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                      )}

                      {/* Logs */}
                      {logs.length > 0 && (
                        <div style={s.logBox}>
                          {logs.map((l, i) => (
                            <div
                              key={i}
                              style={{
                                color: s.logColors[l.type],
                                marginBottom: 2,
                              }}
                            >
                              <span style={{ color: "#475569" }}>
                                [{l.time}]
                              </span>{" "}
                              {l.msg}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tabs */}
                    </div>
                    {/* <p className="input-hint">Examples:</p> */}
                  </div>
                </div>
              </div>

              {/* Right Section - AI Planning Output */}
              <div className="Ai-right-section col-md-6">
                <div className="output-card position-relative">
                  <div className="output-header">
                    <div className="output-title">
                      <span className="output-dot">●</span>
                      <h2>AI Planning Output</h2>
                    </div>
                  </div>
                  <div className="ai-container">
                    <div
                      className="output-content question-output"
                      style={{
                        fontFamily: "monospace",
                        fontSize: 13,
                        color: "#e2e8f0",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {/* File selector tabs (same as pyqExtraction) — each
                          file's own Preview / Questions / JSON are shown
                          below for the selected tab. */}
                      {isMultiFile && (
                        <div
                          style={{
                            display: "flex",
                            gap: 4,
                            flexWrap: "wrap",
                            padding: "8px 8px 0",
                            marginBottom: 8,
                            borderBottom: "1px solid #1e293b",
                            overflowX: "auto",
                          }}
                        >
                          {files.map((entry, idx) => {
                            const isActive = entry.id === selectedEntry?.id;
                            return (
                              <button
                                key={entry.id}
                                title={entry.file.name}
                                style={{
                                  padding: "5px 10px",
                                  cursor: "pointer",
                                  fontSize: 11,
                                  fontWeight: 500,
                                  borderRadius: "6px 6px 0 0",
                                  border: `1px solid ${isActive ? "#6366f1" : "#334155"}`,
                                  borderBottom: "none",
                                  background: isActive ? "#6366f1" : "#1e293b",
                                  color: isActive ? "#fff" : "#94a3b8",
                                  whiteSpace: "nowrap",
                                  maxWidth: 220,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                onClick={() => selectFile(entry.id)}
                              >
                                {idx + 1}. {entry.file.name.replace(/\.[^.]+$/, "")}
                                {entry.status === "extracting" && " ⚡"}
                                {entry.status === "done" &&
                                  ` ✅ ${entry.questions.length}q`}
                                {entry.status === "failed" && " ❌"}
                                {entry.saved && " 💾"}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {(preview || questions.length > 0) && (
                        <>
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              borderBottom: "1px solid #1e293b",
                            }}
                          >
                            {preview && (
                              <button
                                style={s.tab(activeTab === "preview")}
                                onClick={() => setActiveTab("preview")}
                              >
                                📷 Preview
                              </button>
                            )}
                            {questions.length > 0 && (
                              <button
                                style={s.tab(activeTab === "questions")}
                                onClick={() => setActiveTab("questions")}
                              >
                                ❓ Questions ({questions.length})
                              </button>
                            )}
                            {questions.length > 0 && (
                              <button
                                style={s.tab(activeTab === "json")}
                                onClick={() => setActiveTab("json")}
                              >
                                {"{ }"} JSON
                              </button>
                            )}
                          </div>

                          {/* SOP validation errors — shown in the Preview
                              panel so they're impossible to miss, independent
                              of whether a file preview is available. */}
                          {activeTab === "preview" && sopErrors.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                              <div
                                style={{
                                  ...s.card,
                                  border: "1px solid #7f1d1d",
                                  background: "#1f1315",
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 600,
                                    color: "#f87171",
                                    marginBottom: 10,
                                  }}
                                >
                                  ❌ Document failed SOP validation
                                </div>
                                {sopErrors.map((issue, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      padding: "10px 0",
                                      borderTop:
                                        i > 0 ? "1px dashed #334155" : "none",
                                    }}
                                  >
                                    <div style={{ color: "#fca5a5" }}>
                                      {issue.questionNumber != null && (
                                        <strong>
                                          Q{issue.questionNumber}:{" "}
                                        </strong>
                                      )}
                                      {issue.message}
                                    </div>
                                    {issue.fix && (
                                      <div
                                        style={{
                                          marginTop: 4,
                                          color: "#94a3b8",
                                          fontSize: 13,
                                        }}
                                      >
                                        ↳ Fix: {issue.fix}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Preview */}
                          {activeTab === "preview" && preview && (
                            <div style={{ marginTop: 12 }}>
                              {fileType === "image" ? (
                                <img
                                  src={preview}
                                  alt="preview"
                                  style={{
                                    maxWidth: "100%",
                                    borderRadius: 8,
                                    border: "1px solid #334155",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    ...s.card,
                                    textAlign: "center",
                                    color: "#94a3b8",
                                  }}
                                >
                                  📕{" "}
                                  <strong style={{ color: "#a5b4fc" }}>
                                    {file.name}
                                  </strong>
                                  {fileIdRef.current && (
                                    <span
                                      style={{
                                        ...s.badge("#064e3b", "#34d399"),
                                        marginLeft: 8,
                                      }}
                                    >
                                      ✅ Uploaded
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Questions */}
                          {activeTab === "questions" && (
                            <div
                              style={{ marginTop: 12 }}
                              ref={questionsPanelRef}
                            >
                              {/* Stats */}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginBottom: 12,
                                }}
                              >
                                {[
                                  ["Total", questions.length, "#a5b4fc"],
                                  [
                                    "Easy",
                                    questions.filter(
                                      (q) => q.complexityLevel === 1,
                                    ).length,
                                    "#34d399",
                                  ],
                                  [
                                    "Medium",
                                    questions.filter(
                                      (q) => q.complexityLevel === 2,
                                    ).length,
                                    "#fbbf24",
                                  ],
                                  [
                                    "Hard",
                                    questions.filter(
                                      (q) => q.complexityLevel === 3,
                                    ).length,
                                    "#f87171",
                                  ],
                                  [
                                    "MCQ",
                                    questions.filter(
                                      (q) => q.questionType === 1,
                                    ).length,
                                    "#818cf8",
                                  ],
                                  [
                                    "MSQ",
                                    questions.filter(
                                      (q) => q.questionType === 2,
                                    ).length,
                                    "#c084fc",
                                  ],
                                  [
                                    "NAT",
                                    questions.filter(
                                      (q) => q.questionType === 3,
                                    ).length,
                                    "#38bdf8",
                                  ],
                                  [
                                    "Answered",
                                    questions.filter((q) =>
                                      q.options?.some((o) => o.isCorrect),
                                    ).length,
                                    "#818cf8",
                                  ],
                                ].map(([label, val, col]) => (
                                  <div
                                    key={label}
                                    style={{
                                      background: "#1e293b",
                                      borderRadius: 8,
                                      padding: "8px 14px",
                                      textAlign: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: col,
                                        display: "block",
                                      }}
                                    >
                                      {val}
                                    </span>
                                    <span
                                      style={{ fontSize: 11, color: "#64748b" }}
                                    >
                                      {label}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {/* Question cards */}
                              <MathJaxContext config={MATHJAX_CONFIG}>
                                {questions.map((q, i) => {
                                  const complexityLabel = {
                                    1: "Easy",
                                    2: "Medium",
                                    3: "Hard",
                                  };
                                  const complexityStyle = {
                                    1: ["#14532d", "#86efac"],
                                    2: ["#78350f", "#fcd34d"],
                                    3: ["#7f1d1d", "#fca5a5"],
                                  };
                                  const qtypeLabel = {
                                    1: "MCQ",
                                    2: "MSQ",
                                    3: "NAT",
                                  };
                                  const [bg, col] = complexityStyle[
                                    q.complexityLevel
                                  ] ?? ["#1e293b", "#94a3b8"];

                                  return (
                                    <div key={i} style={s.card} className="questionTable">
                                      {/* Header badges */}
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: 6,
                                          flexWrap: "wrap",
                                          alignItems: "center",
                                          marginBottom: 8,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 12,
                                            color: "#6366f1",
                                            fontWeight: 700,
                                          }}
                                        >
                                          Q{q.order}
                                        </span>
                                        {q.section?.sectionId && (
                                          <span
                                            style={s.badge(
                                              "#1e3a5f",
                                              "#60a5fa",
                                            )}
                                          >
                                            Sec {q.section.sectionId}
                                          </span>
                                        )}
                                        <span style={s.badge(bg, col)}>
                                          {complexityLabel[q.complexityLevel] ??
                                            "—"}
                                        </span>
                                        {q.questionType && (
                                          <span
                                            style={s.badge(
                                              "#1e3a5f",
                                              "#818cf8",
                                            )}
                                          >
                                            {qtypeLabel[q.questionType] ??
                                              `Type ${q.questionType}`}
                                          </span>
                                        )}
                                        <span
                                          style={{
                                            marginLeft: "auto",
                                            fontSize: 11,
                                            color: "#475569",
                                          }}
                                        >
                                          <span style={{
                                            color: "#4ade80",
                                          }}>+{q.marks ?? 0}</span> / -
                                          <span style={{
                                            color: "#f87171",
                                          }}>{q.negativeMarks ?? 0}</span>
                                        </span>
                                        {q.unit && (
                                          <span
                                            style={s.badge(
                                              "#1a2e1a",
                                              "#4ade80",
                                            )}
                                          >
                                            {q.unit}
                                          </span>
                                        )}
                                        {q.topicName && (
                                          <span
                                            style={s.badge(
                                              "#F59E0B",
                                              "#2d1c00",
                                            )}
                                          >
                                            {q.topicName}
                                          </span>
                                        )}
                                        {q.subTopicName && (
                                          <span
                                            style={s.badge(
                                              "#A855F7",
                                              "#fff",
                                            )}
                                          >
                                            {q.subTopicName}
                                          </span>
                                        )}

                                      </div>

                                      {/* Question title blocks */}
                                      {q.title?.map((block, idx) => (
                                        <div
                                          key={idx}
                                          style={{
                                            fontSize: 13,
                                            color: "#e2e8f0",
                                            lineHeight: 1.6,
                                            marginBottom: 6,
                                          }}
                                        >
                                          {block.descType === "img" ? (
                                            <img
                                              src={block.description}
                                              alt="question image"
                                              style={{
                                                maxWidth: "100%",
                                                borderRadius: 4,
                                                margin: "6px 0",
                                                display: "block",
                                              }}
                                            />
                                          ) : (
                                            <MathJax>
                                              <span
                                                dangerouslySetInnerHTML={{
                                                  __html: sanitize(
                                                    block.description,
                                                  ),
                                                }}
                                              />
                                            </MathJax>
                                          )}
                                        </div>
                                      ))}

                                      {/* Options — MCQ / MSQ only */}
                                      {q.questionType !== 3 && (
                                        <div style={{ marginTop: 8 }}>
                                          {q.options?.map((opt, j) => (
                                            <div
                                              key={j}
                                              style={s.opt(opt.isCorrect)}
                                            >
                                              <span
                                                style={{
                                                  fontWeight: 700,
                                                  color: opt.isCorrect
                                                    ? "#10b981"
                                                    : "#64748b",
                                                  minWidth: 18,
                                                  fontSize: 13,
                                                }}
                                              >
                                                {String.fromCharCode(65 + j)}.
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: 13,
                                                  color: opt.isCorrect
                                                    ? "#a7f3d0"
                                                    : "#cbd5e1",
                                                }}
                                              >
                                                {opt.descType === "img" ? (
                                                  <img
                                                    src={opt.description}
                                                    alt="question image"
                                                    style={{
                                                      maxWidth: "100%",
                                                      borderRadius: 4,
                                                      margin: "6px 0",
                                                      display: "block",
                                                    }}
                                                  />
                                                ) : (
                                                  <MathJax>
                                                    <span
                                                      dangerouslySetInnerHTML={{
                                                        __html: sanitize(
                                                          opt.description,
                                                        ),
                                                      }}
                                                    />
                                                  </MathJax>
                                                )}
                                              </span>
                                              {opt.isCorrect && (
                                                <span
                                                  style={{
                                                    marginLeft: "auto",
                                                    color: "#10b981",
                                                  }}
                                                >
                                                  ✓
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* NAT answer range */}
                                      {q.questionType === 3 && q.range && (
                                        <div
                                          style={{
                                            marginTop: 8,
                                            padding: "6px 10px",
                                            background: "#0f172a",
                                            borderRadius: 6,
                                            fontSize: 12,
                                            color: "#94a3b8",
                                          }}
                                        >
                                          Answer range:{" "}
                                          <strong style={{ color: "#f0abfc" }}>
                                            {q.range.r1}
                                          </strong>{" "}
                                          to{" "}
                                          <strong style={{ color: "#f0abfc" }}>
                                            {q.range.r2}
                                          </strong>
                                          {q.decimalPlaces > 0 && (
                                            <span
                                              style={{
                                                marginLeft: 8,
                                                color: "#64748b",
                                              }}
                                            >
                                              (up to {q.decimalPlaces} decimal
                                              places)
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      {/* Explanation */}
                                      {q.ansDescription?.text && (
                                        <details style={{ marginTop: 8 }}>
                                          <summary
                                            style={{
                                              cursor: "pointer",
                                              fontSize: 12,
                                              color: "#6366f1",
                                              fontWeight: 600,
                                            }}
                                          >
                                            📖 Explanation
                                          </summary>
                                          <div
                                            style={{
                                              marginTop: 6,
                                              padding: 10,
                                              background: "#0f172a",
                                              borderRadius: 6,
                                              fontSize: 12,
                                              color: "#94a3b8",
                                              lineHeight: 1.7,
                                            }}
                                          >
                                            <MathJax>
                                              <span
                                                dangerouslySetInnerHTML={{
                                                  __html: sanitize(
                                                    q.ansDescription.text,
                                                  ),
                                                }}
                                              />
                                            </MathJax>
                                          </div>
                                        </details>
                                      )}
                                    </div>
                                  );
                                })}
                              </MathJaxContext>
                            </div>
                          )}

                          {/* JSON */}
                          {activeTab === "json" && (
                            <div style={{ marginTop: 12 }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 8,
                                }}
                              >
                                <span
                                  style={{ fontSize: 13, color: "#94a3b8" }}
                                >
                                  {questions.length} questions ready for
                                  database
                                </span>
                                <button
                                  style={s.btn("#10b981", false)}
                                  onClick={copyJSON}
                                >
                                  📋 Copy JSON
                                </button>
                              </div>
                              <div
                                style={{
                                  background: "#0f172a",
                                  border: "1px solid #334155",
                                  borderRadius: 8,
                                  padding: 14,
                                  fontSize: 11,
                                  color: "#94a3b8",
                                  overflow: "auto",
                                  maxHeight: 480,
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "monospace",
                                }}
                              >
                                {JSON.stringify(
                                  {
                                    totalExtracted: questions.length,
                                    questions,
                                  },
                                  null,
                                  2,
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className=" d-flex justify-content-center gap-2 align-items-center my-2 mx-3">
                      <button
                        type="button"
                        className="execute-btn"
                        onClick={() => onHandleSaveData()}
                        disabled={isSaving}
                      >
                        <Zap size={18} />
                        <span>
                          {isMultiFile && file
                            ? selectedEntry?.saved
                              ? `Saved: ${file.name}`
                              : `Save Data: ${file.name}`
                            : "Save Data"}
                        </span>
                      </button>
                      {/* Save All — every extracted, unsaved file becomes
                          its own exam set, saved one by one. */}
                      {isMultiFile && (() => {
                        const pendingSave = files.filter(
                          (e) =>
                            e.status === "done" &&
                            e.questions.length &&
                            !e.saved,
                        ).length;
                        return (
                          <button
                            type="button"
                            className="execute-btn"
                            onClick={() => onHandleSaveData(true)}
                            disabled={isSaving || stage === "extracting" || !pendingSave}
                            style={
                              isSaving || stage === "extracting" || !pendingSave
                                ? { opacity: 0.6, cursor: "not-allowed" }
                                : undefined
                            }
                          >
                            <Zap size={18} />
                            <span>
                              {isSaving
                                ? "Saving..."
                                : `Save All (${pendingSave})`}
                            </span>
                          </button>
                        );
                      })()}
                      {/* Trash Icon */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isOpen && (
          <SubmitExamsetPopup
            isOpen={isOpen}
            exam={exam}
            examStream={examStream}
            setIsOpen={setIsOpen}
            saveExamset={saveExamset}
            activeTemplate={activeTemplate}
            subExam={subExam}
            requireMockPart
          />
        )}
      </div>
      {examOpen && (
        <SubjectNamePopUp
          open={examOpen}
          handleClose={() => setExamOpen(false)}
          selectedList={selectedSubjectList}
          onSelectExams={(data) => setSelectedSubjectList(data)}
          activeTemplate={activeTemplate}
          onSelectTopics={(data) => onHandleTopic(data)}
        />
      )}
    </>
  );
}
